import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { audienceApi } from '@/api/audience';
import { Users } from 'lucide-preact';
import { useLocation } from 'preact-iso';
import { routes } from '@/utils/routes';
import { useAIChatContextUpdater } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
// File status pill (new) - adjust mapping as additional statuses emerge
function FileStatusPill({ status }) {
    const norm = (status || '').toLowerCase();
    const map = {
        processing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        imported: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
        queued: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    };
    const cls = map[norm] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
    const label = status || 'Unknown';
    return (_jsxs("span", { className: `inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${cls}`, title: `File Status: ${label}`, children: [_jsx("span", { className: "opacity-80", children: "File" }), _jsx("span", { className: "w-1 h-1 rounded-full bg-current opacity-60" }), _jsx("span", { children: label })] }));
}
export function AudienceTab({ campaign, audienceAppend }) {
    // Audience files (formerly masterlists)
    const [audienceFiles, setAudienceFiles] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();
    const { updateContext } = useAIChatContextUpdater();
    // Update AI context when viewing campaign audience
    useEffect(() => {
        updateContext({
            workspaceId: campaign.WorkspaceId,
            campaignId: campaign.Id,
            location: ChatLocations.CampaignAudience,
            entityType: ChatEntityTypes.Campaign,
            entityId: campaign.Id,
            viewMode: ChatViewModes.View,
            availableFields: [],
            availableActions: [],
        });
    }, [campaign.Id, campaign.WorkspaceId, updateContext]);
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            const res = await audienceApi.search(campaign.ClientCompanyId, campaign.WorkspaceId, { CampaignId: campaign.Id, CampaignStatuses: ['Completed', 'Imported', 'Processing'] });
            if (res.data) {
                let base = res.data;
                // If an audience file was just created (passed from parent) and not in the fetched list, append it optimistically.
                if (audienceAppend && !base.some(f => f.FileImportId === audienceAppend.FileImportId)) {
                    base = [...base, audienceAppend];
                }
                setAudienceFiles(base);
            }
            else
                setError(res.error || 'Failed to load audience files');
            setLoading(false);
        };
        load();
    }, [campaign.ClientCompanyId, campaign.WorkspaceId, audienceAppend?.FileImportId]);
    // If audienceAppend changes after initial load (e.g., modal created after list rendered), merge it in.
    useEffect(() => {
        if (!audienceAppend)
            return;
        setAudienceFiles(prev => {
            if (!prev)
                return prev;
            if (prev.some(f => f.FileImportId === audienceAppend.FileImportId))
                return prev;
            return [...prev, audienceAppend];
        });
    }, [audienceAppend?.FileImportId]);
    if (loading) {
        return (_jsxs("div", { className: "flex items-center gap-3 text-slate-600 dark:text-slate-300", children: [_jsx("div", { className: "w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), "Loading audience..."] }));
    }
    if (error) {
        return _jsx("div", { className: "text-red-600 dark:text-red-400", children: error });
    }
    if (!audienceFiles || audienceFiles.length === 0) {
        if (loading)
            return null; // already covered above
        return _jsx("div", { className: "text-slate-600 dark:text-slate-300", children: "No audience files found for this workspace." });
    }
    const sorted = useMemo(() => {
        return [...audienceFiles].sort((a, b) => b.TotalContacts - a.TotalContacts);
    }, [audienceFiles]);
    const Card = ({ m }) => {
        const totalRows = m.TotalContacts + m.ErrorCount;
        const successRate = totalRows > 0 ? Math.round((m.TotalContacts / totalRows) * 100) : 0;
        return (_jsxs("button", { type: "button", onClick: () => location.route(routes.scoped(campaign.ClientCompanyId, campaign.WorkspaceId).audienceDetail(m.CampaignId, m.FileImportId, { origin: 'campaign', tab: 'audience' })), className: "text-left bg-white dark:bg-slate-800 rounded-lg shadow-md p-5 border border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500", "aria-label": `Open audience detail for file ${m.FileName}`, children: [_jsxs("div", { className: "flex items-start justify-between mb-3 gap-4", children: [_jsx("div", { className: "flex-1 min-w-0", children: _jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-white truncate", title: m.FileName, children: m.FileName }) }), _jsx("div", { className: "shrink-0", children: _jsx(FileStatusPill, { status: m.FileStatus || 'Unknown' }) })] }), _jsxs("div", { className: "grid grid-cols-3 gap-4", children: [_jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "text-lg font-bold text-slate-900 dark:text-white flex items-center justify-center gap-1", children: [_jsx(Users, { className: "w-4 h-4 text-slate-500" }), m.TotalContacts.toLocaleString()] }), _jsx("div", { className: "text-[11px] text-slate-500 dark:text-slate-400 mt-0.5", children: "Contacts" })] }), _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "text-lg font-bold text-red-600 dark:text-red-400", children: m.ErrorCount.toLocaleString() }), _jsx("div", { className: "text-[11px] text-slate-500 dark:text-slate-400 mt-0.5", children: "Errors" })] }), _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: `text-lg font-bold ${successRate >= 90 ? 'text-green-600 dark:text-green-400' : successRate >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`, children: [successRate, "%"] }), _jsx("div", { className: "text-[11px] text-slate-500 dark:text-slate-400 mt-0.5", children: "Success" })] })] })] }));
    };
    return (_jsx("div", { className: "grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", children: sorted.map(m => (_jsx(Card, { m: m }, m.FileImportId))) }));
}
